import React from 'react';
import { clientWorkspaceHref } from '../../../utils/clientWorkspace';
import { 
  TableContainer, Paper, Button, Typography, Table, TableHead, 
  TableRow, TableCell, Box, IconButton, Menu, MenuItem, Checkbox, TableBody 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Link } from 'react-router-dom';
import SearchBar from '../../../components/Search.js';

const ClinicalView = ({
  currentOrg, userDetails, setAlert, setNewPatient, clearFields, teamMembersData, setCaseOwner,
  searchText, handleSearchChange,
  genderMenuAnchorEl, setGenderMenuAnchorEl, selectedGenderFilters, handleGenderFilterChange,
  statusMenuAnchorEl, setStatusMenuAnchorEl, selectedStatusFilters, handleStatusFilterChange,
  filteredItems, page, rowsPerPage, setPage, handlePageClick,
  mindvaultMap, getLastConsultationComplaint, getLastConsultationDate, isVisualTour
}) => {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: '16px', width: '100%' }}>
      <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button disabled={isVisualTour} onClick={() => {
          if(!currentOrg) {
            setAlert('Please select an organization or go to settings and create an organization first.', 'error');
            return;
          }
          if (!userDetails?.name || !userDetails.name.trim()) {
            setAlert('Please set your username in Profile settings before creating a client.', 'error');
            return;
          }
          setNewPatient(true);  
          clearFields(); 
          const currentTeamMember = teamMembersData.find(member => member.id === userDetails.id);
          if (currentTeamMember) {
            setCaseOwner([currentTeamMember]);
          } else {
            setCaseOwner([]);
          } 
          }} 
          variant="outlined" startIcon={<AddIcon sx={{color: '#fff'}}/>} sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
          <Typography variant="button" sx={{color: '#FFFFFF'}}>New Client</Typography>
        </Button>
        <SearchBar bgcolor='#E4E7EB' placeholder="Search for a client" width="30vw" value={searchText} onChange={handleSearchChange}/>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left"><Typography>Name</Typography></TableCell>
            <TableCell align="left">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
                <Typography align="left" sx={{ mr: 1 }}>Gender</Typography>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGenderMenuAnchorEl(e.currentTarget);
                    }}
                    sx={{ padding: '2px' }}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                  <Menu
                    anchorEl={genderMenuAnchorEl}
                    open={Boolean(genderMenuAnchorEl)}
                    onClose={() => setGenderMenuAnchorEl(null)}
                  >
                    {['All', 'Male', 'Female'].map((option) => (
                      <MenuItem key={option}>
                        <Checkbox
                          checked={selectedGenderFilters.includes(option)}
                          onChange={() => handleGenderFilterChange(option)}
                        />
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
            </TableCell>
            <TableCell align="left"><Typography>Age</Typography></TableCell>
            <TableCell align="left">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
                <Typography align="left" sx={{ mr: 1 }}>Status</Typography>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusMenuAnchorEl(e.currentTarget);
                    }}
                    sx={{ padding: '2px' }}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                  <Menu
                    anchorEl={statusMenuAnchorEl}
                    open={Boolean(statusMenuAnchorEl)}
                    onClose={() => setStatusMenuAnchorEl(null)}
                  >
                    {['All', 'Active', 'Inactive'].map((option) => (
                      <MenuItem key={option}>
                        <Checkbox
                          checked={selectedStatusFilters.includes(option)}
                          onChange={() => handleStatusFilterChange(option)}
                        />
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
            </TableCell>
            <TableCell align="left"><Typography>Phone Number</Typography></TableCell>
            <TableCell align="left"><Typography>Last Consultation</Typography></TableCell>
            <TableCell align="left"><Typography>Presenting Concerns</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((patient, index) => {
              const mv = mindvaultMap[patient.id];

              return (
                <TableRow
                  key={index}
                  component={Link}
                  to={isVisualTour || !currentOrg ? '/consultationSummary' : clientWorkspaceHref(patient.id, currentOrg.id)}
                  data-tour={isVisualTour && patient.id === 'tour-patient-alex-morgan' ? 'demo-patient-row' : undefined}
                  state={{
                    params: {
                      patient: patient,
                      latestComplaint: getLastConsultationComplaint(patient.id),
                      teamMembersData: teamMembersData,
                    },
                  }}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <TableCell align="left">{`${patient.lastName?.toUpperCase() ?? ""} ${patient.firstName ?? ""}`}</TableCell>
                  <TableCell align="left">{patient.gender}</TableCell>
                  <TableCell align="left">{patient.age}</TableCell>
                  <TableCell align="left">{patient.status}</TableCell>
                  <TableCell align="left">{patient.phone_code} {patient.phoneNumber}</TableCell>
                  <TableCell align="left">{getLastConsultationDate(patient.id)}</TableCell>
                  <TableCell align="left">{patient.presenting_concerns}</TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      <div style={{ 
        padding: '16px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Typography>
          Showing data {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredItems.length)} of {filteredItems.length} entries
        </Typography>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            sx={{
              minWidth: '40px',
              height: '40px',
              backgroundColor: '#f0f0f0',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            ←
          </Button>
          {[...Array(Math.ceil(filteredItems.length / rowsPerPage))].map((_, index) => {
            const pageNum = index + 1;
            const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
            const showPage = pageNum <= 3 || 
                          pageNum > totalPages - 3 || 
                          Math.abs(pageNum - (page + 1)) <= 1;

            if (!showPage && pageNum === 4) {
              return <Typography key="ellipsis1">...</Typography>;
            }
            if (!showPage && pageNum === totalPages - 3) {
              return <Typography key="ellipsis2">...</Typography>;
            }
            if (!showPage) return null;

            return (
              <Button
                key={pageNum}
                onClick={() => handlePageClick(pageNum)}
                sx={{
                  minWidth: '40px',
                  height: '40px',
                  backgroundColor: page === index ? '#29C0BB' : '#f0f0f0',
                  color: page === index ? 'white' : 'inherit',
                  '&:hover': { backgroundColor: page === index ? '#29C0BB' : '#e0e0e0' }
                }}
              >
                {pageNum}
              </Button>
            );
          })}
          <Button
            onClick={() => setPage(Math.min(Math.ceil(filteredItems.length / rowsPerPage) - 1, page + 1))}
            disabled={page >= Math.ceil(filteredItems.length / rowsPerPage) - 1}
            sx={{
              minWidth: '40px',
              height: '40px',
              backgroundColor: '#f0f0f0',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            →
          </Button>
        </div>
      </div>
    </TableContainer>
  );
};

export default ClinicalView;
